import React from 'react';
import styled from 'styled-components';
import { FaQrcode, FaMobileAlt, FaShieldAlt, FaCoins, FaChartBar, FaStar } from 'react-icons/fa';
import SectionTitle from '../common/SectionTitle';
import Button from '../common/Button';
import { theme } from '../../styles/GlobalStyles';

const JourneySectionContainer = styled.section`
  padding: 5rem 0;
  background-color: ${props => props.bgColor || theme.bg};
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(to right, transparent, rgba(97,93,255,0.3), transparent);
  }
`;

const JourneyContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const JourneyDescription = styled.p`
  text-align: center;
  max-width: 720px;
  margin: 0 auto 4rem;
  font-size: 1.05rem;
  color: ${theme.textMuted};
  line-height: 1.75;
`;

const JourneyFlow = styled.div`
  position: relative;
  margin-bottom: 4rem;

  &::before {
    content: "";
    position: absolute;
    height: 2px;
    background: ${theme.gradient};
    opacity: 0.25;
    width: 100%;
    top: 5.5rem;
    z-index: 1;
    @media (max-width: 992px) { display: none; }
  }
`;

const JourneySteps = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 0.5rem;
  position: relative;
  z-index: 2;

  @media (max-width: 992px) { grid-template-columns: 1fr; gap: 2rem; }
`;

const StepContainer = styled.div`
  text-align: center;
  &:nth-child(even) {
    margin-top: 5rem;
    @media (max-width: 992px) { margin-top: 0; }
  }
`;

const StepIcon = styled.div`
  width: 88px; height: 88px;
  background: ${theme.cardBg};
  border: 1px solid ${theme.border};
  border-radius: 50%;
  margin: 0 auto 1.25rem;
  display: flex; align-items: center; justify-content: center;
  font-size: 2rem;
  color: #615DFF;
  box-shadow: ${theme.cardShadow};
  position: relative;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    transform: translateY(-8px);
    border-color: rgba(97,93,255,0.4);
    color: #3DD9EB;
    box-shadow: 0 0 30px rgba(97,93,255,0.3), ${theme.cardShadow};
  }

  &::after {
    content: "${props => props.number}";
    position: absolute;
    width: 26px; height: 26px;
    background: ${theme.gradient};
    color: white;
    border-radius: 50%;
    font-size: 0.75rem;
    font-weight: 700;
    display: flex; align-items: center; justify-content: center;
    top: 0; right: 0;
  }
`;

const StepTitle = styled.h3`font-size:0.95rem;font-weight:600;color:${theme.text};margin-bottom:0.5rem;`;
const StepDescription = styled.p`font-size:0.82rem;color:${theme.textMuted};line-height:1.6;`;

const Stakeholders = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 3.5rem;
  @media (max-width: 992px) { grid-template-columns: 1fr; }
`;

const StakeholderCard = styled.div`
  background: ${theme.cardBg};
  border: 1px solid ${theme.border};
  border-radius: 16px;
  backdrop-filter: blur(14px);
  box-shadow: ${theme.cardShadow};
  padding: 2rem;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(97,93,255,0.3);
    box-shadow: 0 12px 40px rgba(97,93,255,0.12), ${theme.cardShadow};
  }

  h3 {
    font-size: 1.2rem;
    color: ${theme.text};
    margin-bottom: 1.25rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;

    svg { color: #615DFF; }
  }
`;

const BenefitsList = styled.ul`
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  li {
    padding-left: 1.4rem;
    position: relative;
    color: ${theme.textMuted};
    line-height: 1.6;
    font-size: 0.9rem;

    &::before {
      content: "✓";
      background: ${theme.gradient};
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      position: absolute;
      left: 0;
      font-weight: 700;
    }
  }
`;

const CtaContainer = styled.div`
  text-align: center;
  margin-top: 2.5rem;
`;

const CustomerJourneySection = ({ bgColor }) => (
  <JourneySectionContainer bgColor={bgColor} id="customer-journey">
    <JourneyContent>
      <SectionTitle>Complete Customer Journey</SectionTitle>
      <JourneyDescription>
        Prodseal creates a seamless authentication experience that builds trust and adds value at every step,
        from initial product scan to reward redemption and beyond.
      </JourneyDescription>

      <JourneyFlow>
        <JourneySteps>
          {[
            { num:1, icon:<FaQrcode />,    title:'Product Purchase',    desc:'Customer buys product with Prodseal QR code protection' },
            { num:2, icon:<FaMobileAlt />, title:'Scan QR Code',        desc:'Customer scans QR code with smartphone camera' },
            { num:3, icon:<FaShieldAlt />, title:'Authentication',      desc:'Instant verification confirms product authenticity' },
            { num:4, icon:<FaCoins />,     title:'Rewards Earned',      desc:'Points automatically added to customer rewards account' },
            { num:5, icon:<FaChartBar />,  title:'Brand Insights',      desc:'Brand receives valuable data in analytics dashboard' },
            { num:6, icon:<FaStar />,      title:'Ongoing Engagement',  desc:'Customer redeems rewards and engages with brand' },
          ].map(({ num, icon, title, desc }) => (
            <StepContainer key={num}>
              <StepIcon number={num}>{icon}</StepIcon>
              <StepTitle>{title}</StepTitle>
              <StepDescription>{desc}</StepDescription>
            </StepContainer>
          ))}
        </JourneySteps>
      </JourneyFlow>

      <Stakeholders>
        <StakeholderCard>
          <h3><FaShieldAlt /> For Brands</h3>
          <BenefitsList>
            <li>Protect revenue and reputation with tamper-proof authentication</li>
            <li>Gain valuable market insights through scan analytics</li>
            <li>Identify counterfeit hotspots to guide enforcement actions</li>
            <li>Engage directly with customers post-purchase</li>
            <li>Build brand trust and loyalty with verification transparency</li>
            <li>Streamline warranty management and reduce fraud</li>
          </BenefitsList>
        </StakeholderCard>

        <StakeholderCard>
          <h3><FaStar /> For Consumers</h3>
          <BenefitsList>
            <li>Instantly verify product authenticity with a simple scan</li>
            <li>Earn reward points for every authentic verification</li>
            <li>Activate product warranties seamlessly</li>
            <li>Access important product information and support</li>
            <li>Redeem points for exclusive offers and discounts</li>
            <li>Enjoy peace of mind with every purchase</li>
          </BenefitsList>
        </StakeholderCard>
      </Stakeholders>

      <CtaContainer>
        <Button primary to="/demo">Try Interactive Demo</Button>
      </CtaContainer>
    </JourneyContent>
  </JourneySectionContainer>
);

export default CustomerJourneySection;
